import React, { useEffect, useMemo, useRef } from 'react';
import { AppLogo } from '@/assets/logos/app-logo';
import { useWebsiteTitlebar } from '../lib/titlebar';
import { Button } from '../components/ui/button';
import {
  BRIDGE_DOWNLOAD_URL,
  openBridgeChatDeepLinkWithFallback,
} from '../components/onboarding/onboarding-open-bridge';

const DownloadIcon = () => (
  <svg
    className="size-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

function isMobileLikeDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  if (/android|iphone|ipod|mobile/.test(userAgent)) {
    return true;
  }

  return /ipad|macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;
}

function triggerDownload() {
  const frame = document.createElement('iframe');
  frame.src = BRIDGE_DOWNLOAD_URL;
  frame.title = 'Bridge download';
  frame.setAttribute('aria-hidden', 'true');
  frame.className = 'hidden';
  document.body.appendChild(frame);
  window.setTimeout(() => frame.remove(), 30_000);
}

function openBridgeOrDownload() {
  openBridgeChatDeepLinkWithFallback({
    openFallback: () => {
      triggerDownload();
    },
  });
}

export function OnboardingDownloadPage(): React.JSX.Element {
  const pageRef = useRef<HTMLDivElement>(null);
  const downloadStartedRef = useRef(false);
  const isMobileDevice = useMemo(() => isMobileLikeDevice(), []);

  useWebsiteTitlebar({ sourceRef: pageRef, light: '#fbfbfa', dark: '#111111' });

  useEffect(() => {
    if (isMobileDevice || downloadStartedRef.current) {
      return;
    }
    downloadStartedRef.current = true;
    openBridgeOrDownload();
  }, [isMobileDevice]);

  return (
    <div ref={pageRef} className="flex min-h-screen flex-col">
      <main className="flex flex-1 items-center">
        <section className="w-full py-12">
          <div className="mx-auto max-w-[1440px] px-6 text-center">
            <AppLogo className="mx-auto mb-8 size-28 text-foreground md:size-32" />
            <h1
              className="mb-4 text-4xl font-medium tracking-tight text-foreground md:text-5xl lg:text-6xl"
              style={{ letterSpacing: '-2px' }}
            >
              Download Bridge
            </h1>
            <p className="mx-auto mb-8 max-w-xl text-base font-medium text-muted-foreground md:text-lg">
              Get the latest version of Bridge for macOS.
            </p>

            <div className="flex flex-col items-center gap-3">
              <Button
                asChild
                size="lg"
                className="h-auto gap-2 rounded-full bg-foreground px-10 py-3 text-base font-medium text-background hover:bg-foreground/90"
              >
                <a href={BRIDGE_DOWNLOAD_URL}>
                  <DownloadIcon />
                  Download Bridge
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
